import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  Activity, Moon, Apple, Pill, MessageSquare, Users, 
  Lightbulb, Clock, Check, Heart, Menu
} from 'lucide-react';
import SidebarNavigation from '../components/SidebarNavigation';

/**
 * Apple App Store Landing Page
 * This version does NOT show any pricing or Stripe-related content
 * to comply with Apple's App Store guidelines.
 */
const AppleLandingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  if (user) {
    navigate('/dashboard');
    return null;
  }

  const features = [
    {
      icon: Activity,
      title: 'Symptom Tracking',
      description: 'Log and monitor symptoms with severity ratings, triggers, and patterns over time.'
    },
    {
      icon: Moon,
      title: 'Sleep Analysis',
      description: 'Track sleep quality, duration, and identify how rest impacts your child\'s condition.'
    },
    {
      icon: Apple,
      title: 'Food Journal',
      description: 'Document meals and discover food sensitivities that may trigger flares.'
    },
    {
      icon: Pill,
      title: 'Medication & Supplements',
      description: 'Manage treatment regimens with reminders and effectiveness tracking.'
    },
    {
      icon: MessageSquare,
      title: 'Voice Journaling',
      description: 'Speak your observations and AI transcribes and categorizes them automatically.'
    },
    {
      icon: Users,
      title: 'Community Support',
      description: 'Connect with other parents on similar journeys. Share experiences and find encouragement.'
    },
    {
      icon: Lightbulb,
      title: 'Smart Correlations',
      description: 'AI-powered analysis discovers hidden patterns between symptoms, triggers, and activities.'
    },
    {
      icon: Clock,
      title: 'Flare Predictions',
      description: 'Get warnings when conditions align for a potential flare based on your tracking data.'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah M.',
      condition: 'PANDAS Parent',
      quote: 'Finally, an app that understands the complexity of PANDAS. The pattern detection helped me identify triggers I never knew my child had.'
    },
    {
      name: 'Michael R.',
      condition: 'PANS Parent',
      quote: 'The AI chat feature is like having a knowledgeable friend available 24/7. It helped me prepare better questions for our neurologist.'
    },
    {
      name: 'Jennifer L.',
      condition: 'Histamine Intolerance',
      quote: 'Tracking symptoms and correlating them with diet and sleep has been eye-opening. We\'ve reduced flare frequency significantly.'
    }
  ];

  const allFeatures = [
    'Symptom tracking & patterns',
    'Sleep & food logging',
    'Flare journal',
    'Knowledge base access',
    'Community forum',
    'Voice journaling with AI transcription',
    'Weather tracking & correlations',
    'Cycle/period tracker',
    'Parent & child regulation tools',
    'AI health chat',
    'Coping library',
    'Smart correlations & trigger detection',
    'Symptom photo log with AI analysis',
    'Flare predictions & warnings',
    'Medical timeline',
    'Lab results tracking',
    'Supplement & medication tracker',
    'Appointment management'
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Sidebar Navigation */}
      <SidebarNavigation isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} showOnLanding={true} />

      {/* Top Banner with Hamburger Menu */}
      <div className="bg-primary text-white py-3 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            data-testid="apple-landing-hamburger-menu"
          >
            <Menu className="w-6 h-6" />
          </button>
          <p className="text-sm md:text-base font-medium">
            Calm Pathways App by Me and My Pandas
          </p>
          <div className="w-10"></div>
        </div>
      </div>

      {/* Hero Section - No pricing CTAs */}
      <section className="relative overflow-hidden py-16 md:py-24 px-6">
        <div className="max-w-4xl mx-auto text-center space-y-6">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Heart className="w-8 h-8 text-primary fill-primary" />
            <span className="text-sm font-medium text-primary bg-primary/10 px-3 py-1 rounded-full">
              Support for PANDAS/PANS Families
            </span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-nunito font-bold text-[#2D2D2D] leading-tight">
            Navigate Your Child's Health Journey with Confidence
          </h1>
          
          <p className="text-lg md:text-xl text-[#57534E] max-w-2xl mx-auto">
            Track symptoms, discover patterns, and take control of your child's health. Our platform helps you understand their condition and make informed decisions.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <Button
              onClick={() => navigate('/auth')}
              className="bg-primary text-white hover:bg-primary/90 rounded-full px-8 py-6 font-bold text-lg"
              data-testid="apple-hero-get-started"
            >
              Get Started
            </Button>
            <Button
              onClick={() => navigate('/auth')}
              variant="outline"
              className="border-2 border-primary text-primary hover:bg-primary/10 rounded-full px-8 py-6 font-bold text-lg"
              data-testid="apple-hero-login"
            >
              Log In
            </Button>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-nunito font-bold text-[#2D2D2D] mb-4">
              Everything You Need to Manage Your Child's Health
            </h2>
            <p className="text-[#57534E] max-w-2xl mx-auto">
              Comprehensive tools designed specifically for the PANDAS/PANS community
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, i) => (
              <Card key={i} className="p-6 hover:shadow-md transition-shadow">
                <feature.icon className="w-10 h-10 text-primary mb-4" strokeWidth={1.5} />
                <h3 className="font-bold text-[#2D2D2D] mb-2">{feature.title}</h3>
                <p className="text-sm text-[#57534E]">{feature.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 px-6 bg-[#FAFAF9]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-nunito font-bold text-[#2D2D2D] mb-4">
              Trusted by the PANDAS/PANS Community
            </h2>
            <p className="text-[#57534E]">
              Hear from real parents managing their children's conditions with Calm Pathways
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <Card key={i} className="p-6 bg-white">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <span className="text-primary font-bold text-lg">{t.name[0]}</span>
                  </div>
                  <div>
                    <p className="font-bold text-[#2D2D2D]">{t.name}</p>
                    <p className="text-sm text-primary">{t.condition}</p>
                  </div>
                </div>
                <p className="text-[#57534E] italic">"{t.quote}"</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Feature List Section - Replaces Pricing */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-nunito font-bold text-[#2D2D2D] mb-4">
              Powerful Features for Your Family
            </h2>
            <p className="text-[#57534E]">
              Everything you need to track, understand, and manage your child's health
            </p>
          </div>
          
          <Card className="p-8 border-2 border-primary">
            <div className="grid md:grid-cols-2 gap-4">
              {allFeatures.map((feature, i) => (
                <div key={i} className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-[#57534E]">{feature}</span>
                </div>
              ))}
            </div>
            <div className="mt-8 text-center">
              <Button
                onClick={() => navigate('/auth')}
                className="bg-primary text-white hover:bg-primary/90 rounded-full px-8 py-4 font-bold"
              >
                Create Your Free Account
              </Button>
            </div>
          </Card>
        </div>
      </section>

      {/* CTA Section - No pricing */}
      <section className="py-16 px-6 bg-primary">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-nunito font-bold text-white mb-4">
            Ready to Take Control of Your Child's Health?
          </h2>
          <p className="text-white/80 mb-8">
            Join families managing PANDAS/PANS smarter.
          </p>
          <Button
            onClick={() => navigate('/auth')}
            className="bg-white text-primary hover:bg-white/90 rounded-full px-8 py-6 font-bold text-lg"
          >
            Get Started Now
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-[#2D2D2D] text-white/60">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-primary fill-primary" />
            <span className="font-bold text-white">Calm Pathways</span>
          </div>
          <div className="flex gap-6 text-sm">
            <Link to="/faq" className="hover:text-white">FAQ's</Link>
            <Link to="/medical-disclosure" className="hover:text-white">Medical Disclaimer</Link>
            <Link to="/privacy-policy" className="hover:text-white">Privacy Policy</Link>
            <Link to="/terms-of-service" className="hover:text-white">Terms of Service</Link>
            <Link to="/contact" className="hover:text-white">Contact</Link>
          </div>
          <p className="text-sm">© 2026 Calm Pathways. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default AppleLandingPage;
